import { API_BASE_URL } from '../config';
import { getAuthTokens } from '../utils/auth';
import api from './middleware';

export const testsuiteApi = {
  // Get all test suite names (lightweight endpoint)
  getTestSuiteNames: async () => {
    try {
      const response = await api.get('/api/testsuite/names');
      return response.data;
    } catch (error) {
      console.error('Get test suite names error:', error);
      throw error;
    }
  },

  // Get all test suites with full details
  getTestSuites: async (sortBy) => {
    try {
      // Only add sort parameter if sortBy has a value
      const url = sortBy 
        ? `/api/testsuite/list?sort=${sortBy}`
        : `/api/testsuite/list`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Get test suites error:', error);
      throw error;
    }
  },

  // Get a single test suite by ID
  getTestSuiteById: async (suiteId) => {
    try {
      const response = await api.get(`/api/testsuite/${suiteId}`);
      return response.data;
    } catch (error) {
      console.error('Get test suite error:', error);
      throw error;
    }
  },

  // Create a new test suite
  createTestSuite: async (testSuiteData) => {
    try {
      const { refreshToken } = getAuthTokens();
      const response = await api.post(
        '/api/testsuite/create',
        {
          ...testSuiteData,
          refresh_token: refreshToken
        }
      );
      return response.data;
    } catch (error) {
      console.error('Create test suite error:', error);
      throw error;
    }
  },

  // Update an existing test suite
  updateTestSuite: async (suiteId, testSuiteData) => {
    try {
      const response = await api.put(
        `/api/testsuite/${suiteId}`,
        testSuiteData
      );
      return response.data;
    } catch (error) {
      console.error('Update test suite error:', error);
      console.error('Full error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Delete a test suite
  deleteTestSuite: async (suiteId) => {
    try {
      const response = await api.delete(`/api/testsuite/${suiteId}`);
      return response.data;
    } catch (error) {
      console.error('Delete test suite error:', error);
      throw error;
    }
  },

  // Batch delete multiple test suites
  batchDeleteTestSuites: async (suiteIds) => {
    try {
      const response = await api.post(
        '/api/testsuite/batch-delete',
        {
          suite_ids: suiteIds
        }
      );
      return response.data;
    } catch (error) {
      console.error('Batch delete test suites error:', error);
      throw error;
    }
  },

  // Get test cases for a specific test suite
  getTestCases: async (suiteId) => {
    try {
      const response = await api.get(`/api/testsuite/${suiteId}/testcases`);
      return response.data;
    } catch (error) {
      console.error('Get test cases error:', error);
      throw error;
    }
  },

  // Run a test suite
  runTestSuite: async (suiteId) => {
    try {
      const response = await api.post(
        '/api/job/run-test-suite',
        {
          test_suite_id: suiteId
        }
      );
      return response.data;
    } catch (error) {
      console.error('Run test suite error:', error);
      throw error;
    }
  },
  
  // Run a test suite with a specific fixture
  runTestSuiteWithFixture: async (suiteId, fixtureId) => {
    try {
      const response = await api.post(
        '/api/job/run-test-suite',
        {
          test_suite_id: suiteId,
          fixture_id: fixtureId
        }
      );
      return response.data;
    } catch (error) {
      console.error('Run test suite with fixture error:', error);
      throw error;
    }
  },

  // Get test cases for a specific test suite (for scheduling)
  getTestCasesForScheduling: async (suiteId) => {
    try {
      const response = await api.get(`/api/testsuite/${suiteId}/testcases/scheduling`);
      return response.data;
    } catch (error) {
      console.error('Get test cases for scheduling error:', error);
      throw error;
    }
  },

  // Create a scheduled job
  createScheduledJob: async (scheduleData) => {
    try {
      const response = await api.post('/api/scheduled-jobs', scheduleData);
      return response.data;
    } catch (error) {
      console.error('Create scheduled job error:', error);
      throw error;
    }
  },

  // Get all scheduled jobs for the current user
  getScheduledJobs: async () => {
    try {
      const response = await api.get('/api/scheduled-jobs');
      return response.data;
    } catch (error) {
      console.error('Get scheduled jobs error:', error);
      throw error;
    }
  },

  // Update a scheduled job
  updateScheduledJob: async (jobId, scheduleData) => {
    try {
      const response = await api.put(`/api/scheduled-jobs/${jobId}`, scheduleData);
      return response.data;
    } catch (error) {
      console.error('Update scheduled job error:', error);
      throw error;
    }
  },

  // Delete a scheduled job
  deleteScheduledJob: async (jobId) => {
    try {
      const response = await api.delete(`/api/scheduled-jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Delete scheduled job error:', error);
      throw error;
    }
  }
};

export default testsuiteApi; 